import { useMemo, useState } from "react";
import currencyCodes from "currency-codes";
import { useOnboarding } from "../context/OnboardingContext";

export default function SummaryStage({
  onEditBankAccounts,
  onEditIncomes,
  onCompleteOnboarding,
}) {
  const { state, getBankAccounts, getIncomes } = useOnboarding();
  const [isCompleting, setIsCompleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const accounts = useMemo(() => {
    return getBankAccounts();
  }, [getBankAccounts]);

  const incomes = useMemo(() => {
    return getIncomes();
  }, [getIncomes]);

  const currencyCode = state.currency || "INR";
  const currencySymbol = useMemo(() => {
    try {
      return currencyCodes.code(currencyCode)?.symbol || currencyCode;
    } catch (_) {
      return currencyCode;
    }
  }, [currencyCode]);

  const totalBalance = useMemo(() => {
    return accounts.reduce((sum, acc) => sum + (Number(acc.balance) || 0), 0);
  }, [accounts]);

  const totalIncome = useMemo(() => {
    return incomes.reduce((sum, inc) => sum + (Number(inc.amount) || 0), 0);
  }, [incomes]);

  const handleCompleteOnboarding = async () => {
    if (!onCompleteOnboarding) return;
    setIsCompleting(true);
    setErrorMessage(null);
    try {
      await onCompleteOnboarding();
    } catch (error) {
      console.error("Failed to complete onboarding:", error);
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setIsCompleting(false);
    }
  };

  const IncomeSummaryComponent = generateIncomeSummary(onEditIncomes, incomes, currencySymbol);


  return (
    <div className="container">
      <div className="row justify-content-center">
        <div className="col-lg-10 col-xl-8">
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-body bg-light">
              <div className="row text-center gy-3">
                <div className="col-md-6">
                  <p className="text-uppercase text-muted mb-1 small">Bank accounts</p>
                  <h2 className="mb-1">{accounts.length}</h2>
                  <p className="text-muted mb-0">
                    Total balance: {currencySymbol}
                    {totalBalance.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
                <div className="col-md-6">
                  <p className="text-uppercase text-muted mb-1 small">Income sources</p>
                  <h2 className="mb-1">{incomes.length}</h2>
                  <p className="text-muted mb-0">
                    Monthly flow: {currencySymbol}
                    {totalIncome.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4 shadow-sm">
            <div className="card-header bg-white d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0">Bank Accounts Summary</h5>
                <small className="text-muted">
                  Review-only mode — jump back if you need to tweak anything.
                </small>
              </div>
              <button className="btn btn-outline-primary btn-sm" onClick={onEditBankAccounts}>
                Edit accounts
              </button>
            </div>
            <div className="card-body">
              {accounts.length === 0 ? (
                <div className="alert alert-warning mb-0">
                  No bank accounts found. Head back to add at least one.
                </div>
              ) : (
                <div className="row g-3">
                  {accounts.map((account, index) => (
                    <div className="col-md-6" key={account.id || index}>
                      <div className="border rounded p-3 h-100">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div>
                            <i className="bi bi-bank me-2"></i>
                            <h6 className="mb-0">{account.name || "Unnamed"}</h6>
                          </div>
                          <span
                            className={`badge ${account.created_at ? "bg-success" : "bg-primary"}`}
                          >
                            {account.created_at ? "Saved" : "New"}
                          </span>
                        </div>
                        <p className="text-muted mb-0">
                          Balance:
                          <span className="ms-1 fw-semibold text-dark">
                            {currencySymbol}
                            {Number(account.balance || 0).toLocaleString()}
                          </span>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {IncomeSummaryComponent}

          {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

          <div className="card shadow-sm border-0">
            <div className="card-body text-center">
              <p className="text-muted mb-3">
                Ready to wrap up? You can always come back to adjust the data before completing
                onboarding.
              </p>
              <button
                className="btn btn-success btn-lg"
                onClick={handleCompleteOnboarding}
                disabled={isCompleting || accounts.length === 0 || incomes.length === 0}
              >
                Complete Onboarding
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const generateIncomeSummary = (onEditIncomes=null, incomes, currencySymbol) => {
  return <div className="card mb-4 shadow-sm">
    <div className="card-header bg-white d-flex justify-content-between align-items-center">
      <div>
        <h5 className="mb-0">Income Summary</h5>
        <small className="text-muted">
          Below are the income sources you've added.
        </small>
      </div>
      {onEditIncomes && (
        <button className="btn btn-outline-primary btn-sm" onClick={onEditIncomes}>
          Edit incomes
        </button>
      )}
    </div>
    <div className="card-body">
      {incomes.length === 0 ? (
        <div className="alert alert-warning mb-0">
          No income sources found. Go back to add at least one.
        </div>
      ) : (
        <div className="row g-3">
          {incomes.map((income, index) => (
            <div key={income.id || index} className="col-md-6">
              <div className="border rounded p-3 h-100">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <i className="bi bi-cash-stack me-2"></i>
                    <h6 className="mb-1">
                      <strong>{income.name || "Income name"}</strong>
                    </h6>
                  </div>
                  <span
                    className={`badge ${income.created_at ? "bg-success" : "bg-primary"}`}
                  >
                    {income.created_at ? "Saved" : "New"}
                  </span>
                </div>
                <p className="mb-1 text-muted">
                  Amount:
                  <span className="ms-1 text-dark fw-semibold">
                    {currencySymbol}
                    {Number(income.amount || 0).toLocaleString()}
                  </span>
                </p>
                <p className="mb-1 text-muted">
                  Frequency:
                  <span className="ms-1 text-dark fw-semibold">
                    {income.frequency_detail?.name || income.frequency_name || "N/A"}
                  </span>
                </p>
                <small className="text-muted">
                  Type: {income.type_detail?.name || income.type_name || "N/A"} • Deposits
                  into:{" "}
                  {income.bank_account_detail?.name ||
                    income.bank_account_name ||
                    `Account ${income.bank_account}`}
                </small>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>;
}
